/**
 * Cliente de sincronização do roteiro.
 *
 * Traduz as mutações do outbox em chamadas HTTP e classifica a resposta nos
 * quatro resultados que `@nv/offline` entende: `ok`, `conflict`, `rejected`,
 * `retry`.
 *
 * A classificação é a parte que importa:
 *
 *   - **rede indisponível ou 5xx → `retry`.** O outbox devolve à fila e para.
 *   - **409 → `conflict`.** Nunca é descartado nem reenviado sozinho; vai
 *     para a folha de conflito com o estado do servidor.
 *   - **4xx que não vai melhorar → `rejected`,** contabilizado e reportado.
 *
 * Confundir `retry` com `rejected` perde escritas; confundir `conflict` com
 * `retry` gera reenvio infinito.
 */

import type { Mutation, SendResult } from '@nv/offline';

export interface SyncConfig {
  baseUrl: string;
  tripId: string;
  /** Avisa o app de que a sessão acabou. Chamado no 401. */
  onUnauthorized?: () => void;
  /**
   * O servidor aceitou: o corpo traz a versão nova. Sem repassá-la ao estado
   * local, a próxima edição da mesma coisa sai com a versão velha e conflita
   * consigo mesma.
   */
  onAccepted?: (mutation: Mutation, body: unknown) => Promise<void> | void;
}

interface ActivityPayload {
  activityId?: string;
  expenseId?: string;
  itemId?: string;
  baseVersion?: number;
  dayId?: string;
  patch?: Record<string, unknown>;
  create?: Record<string, unknown>;
  reorder?: { baseReorderVersion: number; orderedActivityIds: string[] };
}

function rotaDe(config: SyncConfig, mutation: Mutation): { url: string; method: string } | null {
  const payload = mutation.payload as ActivityPayload;
  const base = `${config.baseUrl}/api/v1/trips/${config.tripId}`;

  if (mutation.entity === 'activity' && mutation.operation === 'create') {
    return { url: `${base}/activities`, method: 'POST' };
  }
  if (mutation.entity === 'activity' && mutation.operation === 'update') {
    return { url: `${base}/activities/${payload.activityId}`, method: 'PATCH' };
  }
  if (mutation.entity === 'activity' && mutation.operation === 'delete') {
    return { url: `${base}/activities/${payload.activityId}`, method: 'DELETE' };
  }
  if (mutation.entity === 'itinerary_day' && mutation.operation === 'update') {
    return { url: `${base}/days/${payload.dayId}/reorder`, method: 'POST' };
  }
  // Criar despesa é uma das operações habilitadas offline: um jantar
  // registrado no restaurante sem sinal precisa chegar ao servidor depois.
  if (mutation.entity === 'expense' && mutation.operation === 'create') {
    return { url: `${base}/expenses`, method: 'POST' };
  }
  if (mutation.entity === 'expense' && mutation.operation === 'update') {
    return { url: `${base}/expenses/${payload.expenseId}`, method: 'PATCH' };
  }
  if (mutation.entity === 'expense' && mutation.operation === 'delete') {
    return { url: `${base}/expenses/${payload.expenseId}`, method: 'DELETE' };
  }
  // Bagagem: a lista é feita no quarto do hotel e na véspera, sem rede.
  if (mutation.entity === 'baggage_item' && mutation.operation === 'create') {
    return { url: `${base}/baggage`, method: 'POST' };
  }
  if (mutation.entity === 'baggage_item' && mutation.operation === 'update') {
    const bag = mutation.payload as BaggagePayload;
    return bag.packed === undefined
      ? { url: `${base}/baggage/${bag.itemId}`, method: 'PATCH' }
      : { url: `${base}/baggage/${bag.itemId}/packed`, method: 'PUT' };
  }
  if (mutation.entity === 'baggage_item' && mutation.operation === 'delete') {
    return { url: `${base}/baggage/${payload.itemId}`, method: 'DELETE' };
  }
  return null;
}

interface BaggagePayload {
  itemId?: string;
  packed?: boolean;
  patch?: Record<string, unknown>;
  create?: { items: unknown[] };
}

function corpoDe(mutation: Mutation): unknown {
  const payload = mutation.payload as ActivityPayload;

  if (mutation.entity === 'baggage_item') {
    const bag = mutation.payload as BaggagePayload;
    if (mutation.operation === 'create') return bag.create;
    if (mutation.operation === 'delete') return undefined;
    if (bag.packed !== undefined) return { packed: bag.packed };
    return { baseVersion: mutation.baseVersion, ...bag.patch };
  }
  if (mutation.operation === 'create') return payload.create;
  if (mutation.entity === 'itinerary_day') return payload.reorder;
  if (mutation.operation === 'delete') {
    return { baseVersion: mutation.baseVersion };
  }
  return { baseVersion: mutation.baseVersion, ...payload.patch };
}

export function createSender(config: SyncConfig) {
  return async function send(mutation: Mutation): Promise<SendResult> {
    const rota = rotaDe(config, mutation);
    if (!rota) {
      return { kind: 'rejected', message: `Mutação desconhecida: ${mutation.entity}` };
    }

    let response: Response;
    try {
      const corpo = corpoDe(mutation);
      response = await fetch(rota.url, {
        method: rota.method,
        // A sessão é cookie first-party na mesma origem.
        credentials: 'same-origin',
        headers: {
          // Content-Type só com corpo: JSON vazio é 400 no Fastify.
          ...(corpo === undefined ? {} : { 'content-type': 'application/json' }),
          // Obrigatória. É ela que torna o retry seguro.
          'idempotency-key': mutation.mutationId,
        },
        ...(corpo === undefined ? {} : { body: JSON.stringify(corpo) }),
      });
    } catch (erro) {
      // Sem rede. Devolver à fila e PARAR é o comportamento correto.
      return { kind: 'retry', message: erro instanceof Error ? erro.message : 'offline' };
    }

    if (response.ok) {
      const corpo = (await response.json().catch(() => null)) as { version?: number } | null;
      if (corpo && config.onAccepted) {
        // Falha ao aplicar localmente não desfaz o aceite do servidor.
        await Promise.resolve(config.onAccepted(mutation, corpo)).catch(() => undefined);
      }
      return corpo?.version === undefined
        ? { kind: 'ok' }
        : { kind: 'ok', version: corpo.version };
    }

    if (response.status === 409) {
      const corpo = (await response.json().catch(() => ({}))) as {
        serverState?: unknown;
        message?: string;
      };
      return {
        kind: 'conflict',
        serverState: corpo.serverState ?? null,
        ...(corpo.message ? { message: corpo.message } : {}),
      };
    }

    // 401: a sessão acabou. A mutação NÃO é descartada — fica na fila e sai
    // depois que a pessoa entrar de novo. Descartar aqui perderia trabalho
    // legítimo por causa de um cookie expirado.
    if (response.status === 401) {
      config.onUnauthorized?.();
      return { kind: 'retry', message: 'Sessão expirada. Entre de novo para sincronizar.' };
    }

    // 5xx e 429: o servidor pode melhorar. Vale insistir.
    if (response.status >= 500 || response.status === 429) {
      return { kind: 'retry', message: `Servidor respondeu ${response.status}` };
    }

    // Demais 4xx: insistir não resolve.
    const corpo = (await response.json().catch(() => ({}))) as { message?: string };
    return {
      kind: 'rejected',
      message: corpo.message ?? `Recusado com ${response.status}`,
    };
  };
}

/** Busca o delta desde a última posição conhecida do log. */
export async function fetchChanges(
  config: SyncConfig,
  since: number,
): Promise<{ changes: unknown[]; cursor: number } | null> {
  try {
    const response = await fetch(
      `${config.baseUrl}/api/v1/trips/${config.tripId}/sync?since=${since}`,
      { credentials: 'same-origin' },
    );
    if (!response.ok) return null;
    return (await response.json()) as { changes: unknown[]; cursor: number };
  } catch {
    return null;
  }
}
